// Shared across independently bundled CRM scripts in the same browser page.
const stateKey = Symbol.for('wknd.apiDebug');
const state = globalThis[stateKey] || (globalThis[stateKey] = {
    enabled: true,
    maskToken: false,
    sequence: 0
});
const tokenKey = /token|authorization|api[-_]?key/i;

export function configureApiDebug(options = {}) {
    if (typeof options.enabled === 'boolean') state.enabled = options.enabled;
    if (typeof options.maskToken === 'boolean') state.maskToken = options.maskToken;
    return { enabled: state.enabled, maskToken: state.maskToken };
}

if (typeof window !== 'undefined') {
    window.apiDebug = { configure: configureApiDebug };
}

function redactText(text, secrets) {
    let result = text;
    for (const secret of [...secrets].sort((a, b) => b.length - a.length)) {
        result = result.split(secret).join('[REDACTED]');
        result = result.split(encodeURIComponent(secret)).join('[REDACTED]');
    }
    return result
        .replace(/(Bearer\s+)[^\s"'<>]+/gi, '$1[REDACTED]')
        .replace(/((?:[\w-]*token[\w-]*|authorization|api[-_]?key)["']?\s*[:=]\s*)(?:"(?:\\.|[^"\\])*"|'[^']*'|[^\s&,;<>}]+)/gi, '$1"[REDACTED]"');
}

function collectTokens(value, secrets) {
    if (!value || typeof value !== 'object') return;
    for (const [key, child] of Object.entries(value)) {
        if (tokenKey.test(key) && typeof child === 'string' && child) {
            secrets.add(child);
            if (/^Bearer /i.test(child)) secrets.add(child.slice(7));
        } else {
            collectTokens(child, secrets);
        }
    }
}

function redact(value, secrets) {
    if (typeof value === 'string') return redactText(value, secrets);
    if (Array.isArray(value)) return value.map(item => redact(item, secrets));
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, child]) => [
            key, tokenKey.test(key) ? '[REDACTED]' : redact(child, secrets)
        ]));
    }
    return value;
}

function entriesToObject(entries) {
    const result = Object.create(null);
    for (const [key, value] of entries) {
        if (!(key in result)) result[key] = value;
        else if (Array.isArray(result[key])) result[key].push(value);
        else result[key] = [result[key], value];
    }
    return result;
}

function describeBody(body, contentType = '') {
    if (body == null) return null;
    if (body instanceof URLSearchParams) return entriesToObject(body);
    if (body instanceof FormData) {
        return entriesToObject(Array.from(body, ([key, value]) => [key,
            typeof value === 'string' ? value : {
                name: value.name, type: value.type, size: value.size, lastModified: value.lastModified
            }
        ]));
    }
    if (typeof body === 'string') {
        try { return JSON.parse(body); } catch { /* Plain text or form data. */ }
        if (contentType.includes('application/x-www-form-urlencoded')) {
            return entriesToObject(new URLSearchParams(body));
        }
        return body;
    }
    if (body instanceof Blob) return { type: body.type, size: body.size };
    return String(body);
}

// Exported for non-fetch integrations; never mutate caller data or propagate logging failures.
export function logApiDebug(label, data, secrets = new Set()) {
    if (!state.enabled) return;
    try {
        collectTokens(data, secrets);
        if (typeof data === 'string') {
            try { collectTokens(JSON.parse(data), secrets); } catch { /* Non-JSON response. */ }
        }
        const output = state.maskToken ? redact(data, secrets) : data;
        console.log(label, typeof output === 'string' ? output : JSON.stringify(output, null, 4));
    } catch { /* Debug output must not break an API call. */ }
}

export async function debugFetch(input, init) {
    if (!state.enabled) return globalThis.fetch(input, init);
    const id = ++state.sequence;
    const label = `[API debug #${id}]`;
    const secrets = new Set();
    try {
        const request = typeof Request !== 'undefined' && input instanceof Request ? input : null;
        const url = new URL(request ? request.url : String(input), globalThis.location?.href);
        const headers = new Headers(init?.headers ?? request?.headers);
        let body = init?.body;
        if (body === undefined && request?.body) {
            const copy = request.clone();
            body = headers.get('content-type')?.includes('multipart/form-data')
                ? await copy.formData() : await copy.text();
        }
        const data = {
            method: init?.method ?? request?.method ?? 'GET',
            url: url.href,
            query: entriesToObject(url.searchParams),
            headers: Object.fromEntries(headers),
            parameters: describeBody(body, headers.get('content-type') || '')
        };
        logApiDebug(`${label} Request`, data, secrets);
    } catch (error) {
        logApiDebug(`${label} Request logging error`, { message: String(error) }, secrets);
    }
    try {
        const response = await globalThis.fetch(input, init);
        logApiDebug(`${label} Response`, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers ? Object.fromEntries(response.headers) : {}
        }, secrets);
        // Read a copy asynchronously so streaming and callers' body reads remain unchanged.
        try {
            void response.clone().text().then(raw => {
                logApiDebug(`${label} Raw response`, raw, secrets);
            }).catch(error => {
                logApiDebug(`${label} Response logging error`, { message: String(error) }, secrets);
            });
        } catch (error) {
            logApiDebug(`${label} Response logging error`, { message: String(error) }, secrets);
        }
        return response;
    } catch (error) {
        logApiDebug(`${label} Network error`, { name: error?.name, message: String(error) }, secrets);
        throw error;
    }
}
